import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical,
  Volume2,
  VolumeX,
  PictureInPicture,
  BookOpen,
  User,
  Sun,
  Moon,
  LogOut,
  Globe,
} from "lucide-react";
import type { AppMode } from "../hooks/useTranslator";
import flagUsa from "../../../assets/flag-usa.svg";
import flagArgentina from "../../../assets/-flag-argentina.svg";

interface HeaderKebabMenuProps {
  appMode: AppMode;
  voiceEnabled: boolean;
  pipActive: boolean;
  theme: "light" | "dark";
  uiLang: "en" | "es";
  t: any; // Using the localized strings passed down
  onToggleVoice: () => void;
  onTogglePip: () => void;
  onOpenDocs: () => void;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
  onToggleTheme: () => void;
  onToggleUiLang: () => void;
  onLogout: () => void;
}

export const HeaderKebabMenu: React.FC<HeaderKebabMenuProps> = ({
  appMode,
  voiceEnabled,
  pipActive,
  theme,
  uiLang,
  t,
  onToggleVoice,
  onTogglePip,
  onOpenDocs,
  onOpenProfile,
  onOpenSettings,
  onToggleTheme,
  onToggleUiLang,
  onLogout,
}) => {
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const dropdownEl = document.getElementById("kebab-dropdown");
      if (
        kebabRef.current && !kebabRef.current.contains(target) &&
        !(dropdownEl && dropdownEl.contains(target))
      ) {
        setKebabOpen(false);
      }
    };
    if (kebabOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [kebabOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!kebabOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setKebabOpen((v) => !v);
  };

  const btnBase =
    "inline-flex items-center justify-start text-left gap-1.5 py-1.5 px-3.5 rounded-[6px] border text-sm font-medium transition-all duration-150 active:scale-95 cursor-pointer";
  const btnGhost = `${btnBase} border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)]`;

  return (
    <div className="relative inline-flex items-center" ref={kebabRef}>
      {/* Trigger */}
      <button
        ref={btnRef}
        onClick={handleToggle}
        title={t.moreOptions}
        aria-label={t.moreOptions}
        aria-expanded={kebabOpen}
        aria-haspopup="true"
        className={`inline-flex items-center justify-center py-1.5 px-2.5 rounded-[6px] border text-base font-bold transition-all duration-150 active:scale-95 cursor-pointer ${
          kebabOpen
            ? "border-[var(--border)] bg-white/15 text-[var(--text)]"
            : "border-[var(--border)] bg-transparent text-[var(--text-muted)] hover:bg-white/10 hover:text-[var(--text)]"
        }`}
      >
        <MoreVertical size={16} />
      </button>

      {/* Dropdown — rendered via portal to escape header overflow:hidden */}
      {kebabOpen && createPortal(
        <div
          id="kebab-dropdown"
          className="fixed z-dropdown min-w-[188px] flex flex-col gap-1.5 p-2.5 rounded-[8px] bg-[var(--surface)] border border-[var(--border)] shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
          style={{
            top: dropdownPos.top,
            right: dropdownPos.right,
            animation: "modal-appear 0.18s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          {/* Voice */}
          {appMode === "traductor" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleVoice();
              }}
              title="Activar/Desactivar lectura de voz"
              aria-label={voiceEnabled ? "Desactivar lectura de voz" : "Activar lectura de voz"}
              className={`w-full justify-start ${btnBase} ${
                voiceEnabled
                  ? "bg-[var(--success)]/20 border-[var(--success)]/40 text-[var(--success)] shadow-[0_0_8px_var(--success)]/20"
                  : "bg-[var(--badge-m-bg)] border-[var(--success)]/25 text-[var(--success)] hover:bg-[var(--success)]/20"
              }`}
            >
              {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              <span>{voiceEnabled ? t.voiceOn : t.voiceOff}</span>
            </button>
          )}

          {/* PiP */}
          {appMode === "traductor" && (
            <button
              onClick={() => {
                onTogglePip();
                setKebabOpen(false);
              }}
              title="Ventana PiP flotante"
              aria-label="Alternar ventana PiP flotante"
              className={`w-full justify-start ${btnBase} ${
                pipActive
                  ? "bg-[var(--accent-muted)] border-[var(--accent)]/40 text-[var(--accent)] shadow-[0_0_8px_rgba(0,122,255,0.2)]"
                  : btnGhost
              }`}
            >
              <PictureInPicture size={14} />
              <span>{t.pip}</span>
            </button>
          )}

          {/* Docs */}
          <button
            onClick={() => {
              onOpenDocs();
              setKebabOpen(false);
            }}
            title="Documentación"
            className={`w-full justify-start ${btnGhost}`}
          >
            <BookOpen size={14} />
            <span>{t.docs}</span>
          </button>

          {/* Profile option */}
          <button
            onClick={() => {
              onOpenProfile();
              setKebabOpen(false);
            }}
            title={t.profile}
            className={`w-full justify-start ${btnGhost}`}
          >
            <User size={14} />
            <span>{t.profile}</span>
          </button>


          {/* Separator */}
          <div className="border-t border-[var(--border)] my-1" />

          {/* Theme */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleTheme();
            }}
            title="Cambiar tema visual"
            aria-label={theme === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}
            className={`w-full justify-start ${btnGhost}`}
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            <span>{theme === "dark" ? t.themeLight : t.themeDark}</span>
          </button>

          {/* UI Language toggle */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              onToggleUiLang();
            }}
            title="Cambiar idioma / Change language"
            className="w-full flex items-center bg-[var(--bg3)] rounded-[6px] border border-[var(--b2)] p-1 cursor-pointer"
          >
            <div className={`flex-1 flex items-center justify-center gap-1.5 py-1 rounded-[4px] transition-all text-xs font-bold ${uiLang === 'es' ? 'bg-white dark:bg-[var(--surface)] text-[var(--text)] shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-[var(--text-muted)] hover:text-[var(--text-dim)]'}`}>
              ES <img src={flagArgentina} alt="Argentina" className="w-6 h-6 object-contain" />
            </div>
            <div className={`flex-1 flex items-center justify-center gap-1.5 py-1 rounded-[4px] transition-all text-xs font-bold ${uiLang === 'en' ? 'bg-white dark:bg-[var(--surface)] text-[var(--text)] shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'text-[var(--text-muted)] hover:text-[var(--text-dim)]'}`}>
              EN <img src={flagUsa} alt="USA" className="w-6 h-6 object-contain" />
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={() => {
              onLogout();
              setKebabOpen(false);
            }}
            title={t.logout}
            aria-label="Cerrar sesión"
            className={`w-full justify-start ${btnBase} border-[var(--border)] bg-[var(--surface)] text-rose-400/70 hover:bg-[var(--surface-hover)] hover:text-rose-400`}
          >
            <LogOut size={14} />
            <span>{t.logout}</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};
